import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getLogEntry } from '@/features/elva-work/store';
import type { Doc, DocKind } from '../types';
import { money, parseAmount } from '../utils';
import { renderScan } from './dialogs';

interface Props {
  doc: Doc;
  phase: 'idle' | 'working';
  workLine: string;
  onOpenPage: () => void;
  onSave: (values: Record<string, string>) => void;
  onWait: () => void;
  onCorrect: (correction: string) => void;
  /** A held document: Read it · Read it and trust this sender · Discard. */
  onHeld?: (action: 'read' | 'trust' | 'discard') => void;
  onBack?: () => void;
}

const KIND_CHOICES: { v: DocKind; label: string }[] = [
  { v: 'eob', label: 'EOB' },
  { v: 'request', label: 'Request letter' },
  { v: 'denial', label: 'Denial letter' },
  { v: 'preauth', label: 'Pre-auth answer' },
  { v: 'insurance_card', label: 'Insurance card' },
  { v: 'virtual_card', label: 'Virtual card' },
  { v: 'other', label: 'Other' },
];

function ThisIsntRight({ onCancel, onContinue }: { onCancel: () => void; onContinue: (correction: string) => void }) {
  const [choice, setChoice] = useState('');
  const [kind, setKind] = useState<DocKind>('eob');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('input, button')?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [onCancel]);
  const label = choice === 'kind' ? `a different kind of document (${kind})` : choice;
  return (
    <div className="dc-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div ref={ref} className="dc-dialog" role="dialog" aria-modal="true" aria-label="What’s not right?">
        <h2 className="dc-dialog-title">What’s not right?</h2>
        <fieldset className="dc-radios">
          <legend className="dc-vh">What’s not right</legend>
          <label className="dc-opt">
            <input type="radio" name="dc-wrong" checked={choice === 'kind'} onChange={() => { setChoice('kind'); }} />
            <span>It’s a different kind of document{' '}
              <select className="dc-sel" value={kind} onChange={(e) => { setKind(e.target.value as DocKind); setChoice('kind'); }}>
                {KIND_CHOICES.map((k) => <option key={k.v} value={k.v}>{k.label}</option>)}
              </select>
            </span>
          </label>
          <label className="dc-opt"><input type="radio" name="dc-wrong" checked={choice === 'patient'} onChange={() => { setChoice('patient'); }} /><span>It’s for a different patient or claim</span></label>
          <label className="dc-opt"><input type="radio" name="dc-wrong" checked={choice === 'value'} onChange={() => { setChoice('value'); }} /><span>A value is read wrong</span></label>
          <label className="dc-opt"><input type="radio" name="dc-wrong" checked={choice === 'not-ours'} onChange={() => { setChoice('not-ours'); }} /><span>It isn’t ours</span></label>
        </fieldset>
        <div className="dc-notice">ELVA undoes what it did with this document, applies your correction, and does it again. Every step shows in ELVA’s log.</div>
        <div className="dc-dialog-actions">
          <button type="button" className="dc-btn dc-btn-primary dc-btn-lg" disabled={choice === ''} onClick={() => { onContinue(label); }}>Continue</button>
          <button type="button" className="dc-btn dc-btn-lg" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/** A document from a sender that isn't trusted: held, nothing read yet (D180, D184, D250). */
function HeldBody({ doc, onHeld }: { doc: Doc; onHeld: ((action: 'read' | 'trust' | 'discard') => void) | undefined }) {
  const h = doc.held;
  if (!h) return null;
  if (h.resolved) {
    return (
      <section className="dc-card dc-card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div className="dc-tick"><span className="dc-ok" aria-hidden="true">✓</span><span>{h.resolved === 'trust' ? `You chose Read it and trust this sender. ELVA is reading it, and ${h.sender} is now a trusted ${h.via === 'fax' ? 'fax number' : 'sender'}.` : 'You chose Read it. ELVA is reading it this once; the sender stays untrusted.'}</span></div>
        <p className="dc-meta" style={{ margin: 0 }}>{doc.history}</p>
      </section>
    );
  }
  const pageWord = `${String(h.pages)} page${h.pages === 1 ? '' : 's'}`;
  return (
    <>
      <section className="dc-card dc-card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h3 className="dc-h3">ELVA hasn’t read this yet</h3>
        <dl className="dc-kv">
          <dt>Arrived</dt><dd>{h.arrived} · by {h.via}</dd>
          <dt>Sender</dt><dd>{h.sender}</dd>
          <dt>Pages</dt><dd>{pageWord}</dd>
          {h.files ? <><dt>Files</dt><dd>{h.files.join(', ')}</dd></> : null}
          <dt>ELVA thinks</dt><dd>{h.looksLike}</dd>
        </dl>
        {h.failedChecks ? <div className="dc-notice">This email may not really be from {h.sender}. It failed the sender checks, so it is held even if the address looks familiar.</div> : null}
        <p className="dc-meta" style={{ margin: 0 }}>
          Fake EOBs and letters are a real risk, so documents from senders you haven’t trusted are held until a person says. ELVA’s guess comes from the first glance, not from reading it.
        </p>
      </section>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', paddingTop: 14, borderTop: '1px solid var(--border)' }}>
        <button type="button" className="dc-btn dc-btn-primary dc-btn-lg" onClick={() => { onHeld?.('read'); }}>Read it</button>
        <button type="button" className="dc-btn dc-btn-lg" onClick={() => { onHeld?.('trust'); }}>Read it and trust this sender</button>
        <button type="button" className="dc-btn dc-btn-ghost" onClick={() => { onHeld?.('discard'); }}>Discard</button>
      </div>
      <p className="dc-meta" style={{ padding: '0 4px' }}>{doc.history}</p>
    </>
  );
}

export function DocDetail({ doc, phase, workLine, onOpenPage, onSave, onWait, onCorrect, onHeld, onBack }: Props) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [wrongOpen, setWrongOpen] = useState(false);

  useEffect(() => {
    headingRef.current?.focus();
    setValues({});
    setWrongOpen(false);
  }, [doc.id]);

  const page0 = doc.pages[0];

  // needs-you validation
  const fields = doc.fields ?? [];
  const allFilled = fields.length > 0 && fields.every((f) => (values[f.id] ?? '').trim() !== '');
  const sum = fields.reduce((s, f) => s + parseAmount(values[f.id] ?? ''), 0);
  const constraint = doc.constraint;
  const constraintMet = !constraint || Math.abs(sum - constraint.total) < 0.005;
  const canSave = allFilled && constraintMet;

  return (
    <main className="dc-detail">
      {onBack ? <button type="button" className="dc-back" onClick={onBack}>← All documents</button> : null}
      <div className="dc-detail-inner">
        <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className="dc-kind" style={{ alignSelf: 'flex-start' }}>{doc.kindLabel}</span>
          <h2 className="dc-doctitle" tabIndex={-1} ref={headingRef}>{doc.title}</h2>
          <div className="dc-meta">{doc.metaLine}</div>
          {doc.held?.deadline ? <span className="dc-badge b-danger" style={{ alignSelf: 'flex-start' }}>Deadline {doc.held.deadline.label}</span> : null}
        </section>

        {doc.held ? (
          <HeldBody doc={doc} onHeld={onHeld} />
        ) : doc.status === 'needs' ? (
          <>
            {/* tries */}
            <section className="dc-card" style={{ overflow: 'hidden' }}>
              <div style={{ padding: '12px 18px 6px' }}><strong style={{ fontSize: 14.5 }}>What ELVA tried first</strong></div>
              {doc.tries?.map((t, i) => (
                <div key={`try-${String(i)}`} className="dc-try">
                  <span className="t">{t.time}</span>
                  <span className="dc-try-main"><span className="dc-x" aria-hidden="true">×</span><span><strong>{t.head}</strong> {t.body}</span></span>
                  {getLogEntry(t.logId) ? <Link className="dc-src" to={`/elva-work/log/${t.logId}`}>Log</Link> : <span />}
                </div>
              ))}
            </section>
            {/* crop + fields */}
            <div className="dc-two">
              {page0 ? (
                <figure style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
                  <div className="dc-scan tilt" role="img" aria-label={page0.alt}>{renderScan(page0.text)}</div>
                  <figcaption className="dc-meta">{page0.caption} · <button type="button" className="dc-src" onClick={onOpenPage}>Open the full page</button></figcaption>
                </figure>
              ) : null}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
                <div style={{ fontSize: 14.5, color: 'var(--fg2)' }}>Read the unreadable values on the paper EOB and type them here. {constraint?.text}</div>
                {fields.map((f) => (
                  <label key={f.id} className="dc-flabel">
                    <span>{f.label}</span>
                    <input className="dc-field num" style={{ maxWidth: 180 }} inputMode="decimal" value={values[f.id] ?? ''} onChange={(e) => { setValues((v) => ({ ...v, [f.id]: e.target.value })); }} />
                  </label>
                ))}
                {constraint && allFilled ? (
                  <div className={`dc-constraint ${constraintMet ? 'ok' : 'bad'}`}>
                    These add up to {money(sum)}; they should add up to {money(constraint.total)}.
                  </div>
                ) : null}
                <div className="dc-meta">Everything else on the page is read and checked.</div>
                <button type="button" className="dc-btn dc-btn-ghost" style={{ alignSelf: 'flex-start' }} onClick={() => { setWrongOpen(true); }}>This isn’t right</button>
              </div>
            </div>
            {phase === 'working' ? (
              <div className="dc-live" aria-live="polite">{workLine || 'ELVA is working…'}</div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                <button type="button" className="dc-btn dc-btn-primary dc-btn-lg" disabled={!canSave} aria-describedby={!canSave ? 'dc-save-hint' : undefined} onClick={() => { onSave(values); }}>Save and let ELVA post it</button>
                {doc.waitForClean ? <button type="button" className="dc-btn dc-btn-ghost" onClick={onWait}>Wait for the clean copy</button> : null}
                <span id={!canSave ? 'dc-save-hint' : undefined} className="dc-meta" style={{ marginLeft: 'auto' }}>
                  {!canSave ? 'Type every amount; they must add up. ' : ''}
                  This also shows in Human in the loop:{' '}
                  {doc.hitlItemId
                    ? <Link className="dc-src" to={`/human-in-the-loop/${doc.hitlItemId}`}>Read a document</Link>
                    : 'Read a document'}
                  .
                </span>
              </div>
            )}
          </>
        ) : doc.status === 'filed' ? (
          <>
            <section className="dc-card dc-card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {doc.filedTicks?.map((t, i) => (
                <div key={`f-${String(i)}`} className="dc-tick"><span className="dc-ok" aria-hidden="true">✓</span><span>{t}</span></div>
              ))}
            </section>
            <p className="dc-meta">Listed under “Filed, no work needed”. Faxes ELVA can’t match to any patient stay here, unfiled, for 30 days, then are deleted.</p>
            <p className="dc-meta" style={{ padding: '0 4px' }}>{doc.history}</p>
          </>
        ) : (
          <>
            <div className="dc-two">
              {page0 ? (
                <figure className="dc-card" style={{ margin: 0, padding: 14, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
                  <div className="dc-scan tilt" role="img" aria-label={page0.alt}>{renderScan(page0.text)}</div>
                  <figcaption className="dc-meta">{page0.caption} · <button type="button" className="dc-src" onClick={onOpenPage}>Open the full page</button></figcaption>
                </figure>
              ) : null}
              <section className="dc-card" style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
                <h3 className="dc-h3">What ELVA read</h3>
                <dl className="dc-kv">
                  {doc.read.map((r, i) => (
                    <div key={`kv-${String(i)}`} style={{ display: 'contents' }}>
                      <dt>{r.label}</dt>
                      <dd>{r.value}{r.note ? <span className="note">{r.note}</span> : null}</dd>
                    </div>
                  ))}
                </dl>
                <button type="button" className="dc-btn dc-btn-ghost" style={{ alignSelf: 'flex-start' }} onClick={() => { setWrongOpen(true); }}>This isn’t right</button>
              </section>
            </div>

            <section className={`dc-card dc-didcard ${doc.doneTone}`}>
              <div className="dc-didhead">
                <span className={`dc-badge ${doc.doneTone === 'done' ? 'b-ok' : 'b-info'}`} style={{ background: '#fff' }}>{doc.doneTone === 'done' ? 'Done' : 'In progress'}</span>
                <h3 className="dc-h3">{doc.doneTitle}</h3>
              </div>
              <div className="dc-didbody">
                {doc.doneSteps.map((s, i) => (
                  <div key={`st-${String(i)}`} className="dc-tick"><span className="dc-ok" aria-hidden="true">✓</span><span>{s.time ? `${s.time} · ` : ''}{s.text}</span></div>
                ))}
                {doc.doneLinks.length > 0 ? (
                  <div className="dc-srcs">{doc.doneLinks.map((l, i) => <Link key={`ln-${String(i)}`} className="dc-src" to={l.href}>{l.label}</Link>)}</div>
                ) : null}
              </div>
            </section>
            {phase === 'working' ? <div className="dc-live" aria-live="polite">{workLine || 'ELVA is working…'}</div> : null}
            <p className="dc-meta" style={{ padding: '0 4px' }}>{doc.history}</p>
          </>
        )}
      </div>

      {wrongOpen ? (
        <ThisIsntRight
          onCancel={() => { setWrongOpen(false); }}
          onContinue={(correction) => { setWrongOpen(false); onCorrect(correction); }}
        />
      ) : null}
    </main>
  );
}
